// Include polyfills for Set methods
import "./polyfills"
// Include phoenix_html to handle method=PUT/DELETE in forms and buttons.
import "phoenix_html"
// Establish Phoenix Socket and LiveView configuration.
import { Socket } from "phoenix"
import { LiveSocket } from "phoenix_live_view"
import topbar from "topbar"
import Alpine from "alpinejs"
import Uploaders from "./uploaders"
import { zoomLevel } from "zoom-level"
import {
  initializeIntercom,
  updateIntercom,
  showIntercom,
  showNewMessage,
} from "./intercom"
// Start user socket before hooks are loaded
import "./user_socket.js"
import allHooks from "./hooks"
import alpineFns from "./alpine_functions"
import "./quill_setup"
import { initCidDebug, initOrphanedCidDetector } from "./cid_debug"
import { phxFeedbackDom } from "./phx_feedback_dom"
import { patchTooltipDelay } from "./utils/highcharts_tooltip_delay"
import { applyHighchartsTheme } from "./utils/highcharts_theme"
import { Hooks as FluxonHooks, DOM as FluxonDOM } from "../../deps/fluxon"
import { withCompactAccordionMode } from "./fluxon_accordion_hook"
import { initializeMobileWebViewNavigation } from "./mobile_webview_navigation.mjs"
import { searchPrivacy } from "./search_privacy.mjs"
import { initializeFetchEmailDiagnostics } from "./fetch_email_diagnostics.mjs"

FluxonHooks["Fluxon.Accordion"] = withCompactAccordionMode(
  FluxonHooks["Fluxon.Accordion"],
)

// Patch Fluxon.DatePicker hook to sync disabled/required state and handle
// invalid typed dates.
//
// Four concerns are managed here:
// 1. Disabled-state sync — the calendar wrapper uses phx-update="ignore" so
//    LiveView can't update month/year selects; we sync from our wrapper attribute.
// 1b. Required-state sync — Fluxon has no `required` attribute at all, so the
//    constraint is stamped on the wrapper and applied to the typeable input here.
// 2. Duplicate phx-change prevention — the typeable text input has no `name`
//    so its `input` events must be stopped before they reach LiveView.
// 3. Invalid-date preservation — when Fluxon fails to parse a typed date it
//    clears the typeable input silently. We capture the raw text on blur,
//    detect the failed parse via requestAnimationFrame (after Fluxon's own
//    handlers settle), restore the visible text, inject an error span, and
//    block form submission while invalid so the empty canonical hidden input
//    cannot be saved as nil. Works for every date_picker in the app without
//    per-schema changes.
const _origDatePickerHook = FluxonHooks["Fluxon.DatePicker"]
FluxonHooks["Fluxon.DatePicker"] = {
  mounted() {
    _origDatePickerHook.mounted.call(this)
    this._syncDisabledState()
    this._syncRequiredState()
    this._stopTypeableInputPropagation()
    this._installInvalidDateListeners()
  },
  updated() {
    _origDatePickerHook.updated.call(this)
    this._syncDisabledState()
    this._syncRequiredState()
    this._stopTypeableInputPropagation()
    this._reapplyInvalidStateIfNeeded()
  },
  destroyed() {
    _origDatePickerHook.destroyed.call(this)
    if (this._submitBlocker && this._submitBlockerForm) {
      this._submitBlockerForm.removeEventListener(
        "submit",
        this._submitBlocker,
        { capture: true },
      )
    }
  },
  _stopTypeableInputPropagation() {
    // The typeable text input has no `name` attribute, so its `input` events
    // reach LiveView with _target="" — causing a duplicate validate with wrong
    // field targeting that conflicts with the hidden input's correct `change`
    // event. Same pattern as NumberInput (number_input_hook.js:45-47).
    const typeableInput = this.el.querySelector(
      '[data-part="typeable-text-input"]',
    )
    if (typeableInput) {
      typeableInput.addEventListener("input", (e) => e.stopPropagation())
    }
  },
  _syncRequiredState() {
    // Same gap as disabled: Fluxon has no `required` attribute, and its named
    // input is `hidden`, where `required` makes the form unsubmittable with an
    // unfocusable-control error. The visible typeable input is the right owner
    // — it is focusable and carries no `name`, so constraint validation runs
    // without touching the submitted params.
    const wrapper = this.el.closest("[data-fluxon-wrapper]")
    const isRequired = wrapper
      ? wrapper.hasAttribute("data-dp-required")
      : false

    const typeableInput = this.el.querySelector(
      '[data-part="typeable-text-input"]',
    )
    if (typeableInput) typeableInput.required = isRequired

    // Non-typeable pickers expose only the toggle button, which cannot carry a
    // native constraint; announce the requirement instead.
    const toggle = this.el.querySelector('[data-part="toggle"]')
    if (toggle && !typeableInput) {
      toggle.setAttribute("aria-required", String(isRequired))
    }
  },
  _syncDisabledState() {
    // Read disabled from our wrapper's data attribute (set by inputs.ex),
    // since Fluxon doesn't apply disabled to typeable mode elements.
    const wrapper = this.el.closest("[data-fluxon-wrapper]")
    const isDisabled = wrapper
      ? wrapper.hasAttribute("data-dp-disabled")
      : false

    const toggle = this.el.querySelector('[data-part="toggle"]')
    if (toggle) toggle.disabled = isDisabled

    const typeableInput = this.el.querySelector(
      '[data-part="typeable-text-input"]',
    )
    if (typeableInput) {
      typeableInput.disabled = isDisabled
      typeableInput.tabIndex = isDisabled ? -1 : 0
    }

    this.el
      .querySelectorAll('[data-part="month-select"], [data-part="year-select"]')
      .forEach((sel) => (sel.disabled = isDisabled))
  },
  _getWrapper() {
    return this.el.closest("[data-fluxon-wrapper]")
  },
  _installInvalidDateListeners() {
    // Guard against duplicate registration across mounted/updated cycles.
    if (this._invalidListenersInstalled) return
    this._invalidListenersInstalled = true

    // Capture phase fires before Fluxon's own blur handler on the typeable input.
    // We store the raw text, then schedule _evaluateParseResult via rAF so that
    // Fluxon's synchronous clear runs first.
    this.el.addEventListener(
      "blur",
      (e) => {
        const typeableInput = this.el.querySelector(
          '[data-part="typeable-text-input"]',
        )
        if (e.target !== typeableInput) return
        const rawText = typeableInput.value
        requestAnimationFrame(() => this._evaluateParseResult(rawText))
      },
      { capture: true },
    )

    // When the user starts typing again, clear our invalid state so a fresh
    // parse attempt can succeed without a stale error span in the way.
    const typeableInput = this.el.querySelector(
      '[data-part="typeable-text-input"]',
    )
    if (typeableInput) {
      typeableInput.addEventListener("input", () => this._clearInvalidState())
    }

    // Block form submission when the date picker is in an invalid state. The
    // canonical hidden input is empty when parsing failed, so without this
    // guard the form would submit nil for the date — silently saving "no
    // date" while the user sees their text and an error message in the UI.
    //
    // We have to evaluate invalid-ness *synchronously* here: blur schedules
    // _evaluateParseResult via rAF, and pressing Enter (which fires submit
    // before that rAF callback runs) would otherwise sneak through. So we
    // check the canonical-vs-typeable values directly rather than relying on
    // the asynchronously-set _invalidRaw flag, and call _setInvalidState if
    // we discover invalid state we hadn't yet recorded. Capture phase beats
    // LiveView's submit handler.
    const form = this.el.closest("form")
    if (form) {
      this._submitBlocker = (e) => {
        const typeableInput = this.el.querySelector(
          '[data-part="typeable-text-input"]',
        )
        const canonicalInput = this.el.querySelector('[data-part="input"]')
        const typedText = typeableInput ? typeableInput.value : ""
        const canonicalValue = canonicalInput ? canonicalInput.value : ""
        const looksInvalid = typedText.trim() !== "" && canonicalValue === ""

        if (!this._invalidRaw && !looksInvalid) return

        if (looksInvalid && !this._invalidRaw) {
          this._setInvalidState(typedText)
        }

        e.preventDefault()
        e.stopImmediatePropagation()
        if (typeableInput) typeableInput.focus()
      }
      form.addEventListener("submit", this._submitBlocker, { capture: true })
      this._submitBlockerForm = form
    }
  },
  _evaluateParseResult(rawText) {
    // Called after Fluxon's blur handler has run (via requestAnimationFrame).
    // If the canonical hidden is now empty, Fluxon failed to parse rawText.
    const canonicalInput = this.el.querySelector('[data-part="input"]')
    const canonicalValue = canonicalInput ? canonicalInput.value : ""

    if (canonicalValue) {
      // Valid parse — clear any stale invalid state.
      this._clearInvalidState()
    } else if (rawText && rawText.trim() !== "") {
      // Failed parse with non-empty input — show the invalid-date UX.
      this._setInvalidState(rawText)
    }
  },
  _setInvalidState(rawText) {
    this._invalidRaw = rawText

    const typeableInput = this.el.querySelector(
      '[data-part="typeable-text-input"]',
    )
    if (typeableInput) {
      typeableInput.value = rawText
      typeableInput.setAttribute("aria-invalid", "true")
    }

    this._ensureErrorSpan()
  },
  _getErrorContainer() {
    const wrapper = this._getWrapper()
    return wrapper
      ? wrapper.querySelector("[data-enaia-date-error-container]")
      : null
  },
  _clearInvalidState() {
    this._invalidRaw = null

    // Remove only the span we injected. Leave server-rendered error_tag spans alone.
    const container = this._getErrorContainer()
    if (container) {
      container
        .querySelectorAll("[data-enaia-date-error]")
        .forEach((el) => el.remove())
    }
  },
  _ensureErrorSpan() {
    const wrapper = this._getWrapper()
    if (!wrapper) return

    // If the server has already rendered a visible error span for this field,
    // don't add a duplicate. We check :not(.phx-no-feedback) because
    // phxFeedbackDom hides the server span until the form is submitted (the
    // canonical hidden input is set programmatically by Fluxon and is never
    // marked PHX_HAS_FOCUSED).
    const canonicalInput = this.el.querySelector('[data-part="input"]')
    const fieldName = canonicalInput ? canonicalInput.name : null
    if (fieldName) {
      const visibleServerSpan = wrapper.querySelector(
        `.phx-error-message[phx-feedback-for="${fieldName}"]:not(.phx-no-feedback)`,
      )
      if (visibleServerSpan) return
    }

    const container = this._getErrorContainer()
    if (!container) return

    // Already injected.
    if (container.querySelector("[data-enaia-date-error]")) return

    const message =
      wrapper.dataset.enaiaDateInvalidMessage || "is not a valid date format"

    const span = document.createElement("span")
    span.className = "phx-error-message"
    // No phx-feedback-for: the canonical hidden input is never marked "used" by
    // LiveView (Fluxon sets it programmatically), so phxFeedbackDom would add
    // phx-no-feedback and hide the span. JS owns this span's lifecycle via the
    // phx-update="ignore" container — morphdom leaves the container's children
    // untouched so the span persists through LiveView patches.
    span.setAttribute("data-enaia-date-error", "true")
    span.textContent = message

    container.appendChild(span)
  },
  _reapplyInvalidStateIfNeeded() {
    // After LiveView re-renders, morphdom resets the typeable input to "".
    // If we still have a stored invalid value and the canonical is still empty,
    // re-apply so the user continues to see their text and the error message.
    if (!this._invalidRaw) return

    const canonicalInput = this.el.querySelector('[data-part="input"]')
    if (canonicalInput && canonicalInput.value) {
      // A valid date appeared (e.g. server corrected it) — clear our state.
      this._clearInvalidState()
      return
    }

    this._setInvalidState(this._invalidRaw)
  },
}
import { hooks as colocatedHooks } from "phoenix-colocated/enaia"
import { hooks as appColocatedHooks } from "phoenix-colocated/enaia/app.js"
import {
  PASTEL_MAP_STYLES,
  DESATURATED_MAP_STYLES,
  PIN_COLORS,
  createCustomPinIcon,
} from "./google_maps_styles"
import { loadGoogleMaps, googleMapsDynamicKey } from "./google_maps_loader"
import FormSnapshotter from "./form_snapshotter"
import { Unload } from "./unload"
import Formatters from "./formatters"

window.alpineFns = alpineFns
window.FormSnapshotter = FormSnapshotter
window.Unload = Unload

// Make utilities available for colocated hooks (scoped under Enaia namespace)
window.Enaia = window.Enaia || {}
window.Enaia.GoogleMapsStyles = {
  PASTEL_MAP_STYLES,
  DESATURATED_MAP_STYLES,
  PIN_COLORS,
  createCustomPinIcon,
}
window.Enaia.loadGoogleMaps = loadGoogleMaps
window.Enaia.googleMapsDynamicKey = googleMapsDynamicKey
window.Enaia.Formatters = Formatters
window.Enaia.patchTooltipDelay = patchTooltipDelay
window.Enaia.applyHighchartsTheme = applyHighchartsTheme

let csrfToken = document
  .querySelector("meta[name='csrf-token']")
  .getAttribute("content")
let liveSocket = new LiveSocket("/live", Socket, {
  uploaders: Uploaders,
  hooks: {
    ...allHooks,
    ...FluxonHooks,
    ...colocatedHooks,
    ...appColocatedHooks,
  },
  params: () => ({
    _csrf_token: csrfToken,
    screenHeight: screen.height,
    screenWidth: screen.width,
    zoomLevel: zoomLevel(),
    browser_locale: navigator.language || navigator.userLanguage || "en-US",
    appcues_identified_user: window.appcuesIdentifiedUser || null,
  }),
  dom: phxFeedbackDom({
    onBeforeElUpdated(from, to) {
      for (const attr of from.attributes) {
        if (attr.name.startsWith("x-data")) {
          to.setAttribute(attr.name, attr.value)
        }
      }
      if (from.nodeType === 1 && from._x_dataStack) {
        Alpine.clone(from, to)
      }

      FluxonDOM.onBeforeElUpdated(from, to)
    },
  }),
  viewLogger: (view, kind, msg, obj) => {
    if (view.liveSocket.isDebugEnabled()) {
      // LiveView's default code:
      //   console.log(`${view.id} ${kind}: ${msg} - `, obj)
      // `msg` seems always empty, and `obj` is a complex payload that is better skipped
      console.log(`[LV] ${kind} (${view.id})`)
    }
  },
})

// Show progress bar on live navigation and form submits
topbar.config({ barColors: { 0: "#29d" }, shadowColor: "rgba(0, 0, 0, .3)" })
let topBarScheduled = undefined

window.addEventListener("phx:page-loading-start", () => {
  if (!topBarScheduled) {
    topBarScheduled = setTimeout(() => topbar.show(), 200)
  }
})
window.addEventListener("phx:page-loading-stop", () => {
  clearTimeout(topBarScheduled)
  topBarScheduled = undefined
  topbar.hide()
})

window.addEventListener("phx:hide-modal", (e) =>
  document.querySelector("[data-modal-id=" + e.detail.modal_id + "]")?.click(),
)

// Force showing form errors by removing the class that hides them as part of
// custom validation flows (such as the Review UI's partial per-step validation).
//
// When the server includes on_clear (a JSON-encoded LiveView JS command) in
// the detail, it is executed if NO visible errors are found — allowing the
// server to proceed (e.g. navigate to the next tab).
window.addEventListener("phx:show-form-errors", (e) => {
  const form = document.getElementById(e.detail.form_id)
  if (!form) return

  const isVisible = (el) => {
    if (!el) return false
    if (el.closest("[hidden], .hidden")) return false

    const style = window.getComputedStyle(el)
    if (style.display === "none" || style.visibility === "hidden") return false

    return el.getClientRects().length > 0
  }

  const cssEscape = (value) => {
    if (window.CSS?.escape) return window.CSS.escape(value)
    return String(value).replace(/([^\w-])/g, "\\$1")
  }

  const findInput = (root) => {
    if (!root) return null
    const selector =
      "input:not([type='hidden']):not([disabled]), select:not([disabled]), textarea:not([disabled])"
    return Array.from(root.querySelectorAll(selector)).find(isVisible) || null
  }

  form.querySelectorAll("[phx-feedback-for]").forEach((el) => {
    el.classList.remove("phx-no-feedback")
  })

  // Scroll to and focus the first invalid field
  requestAnimationFrame(() => {
    const firstError = Array.from(
      form.querySelectorAll(".phx-error-message"),
    ).find(isVisible)

    if (firstError) {
      const feedbackFor = firstError.getAttribute("phx-feedback-for")

      const feedbackContainer =
        feedbackFor &&
        form.querySelector(`[phx-feedback-for="${cssEscape(feedbackFor)}"]`)

      const inputByName =
        feedbackFor && form.querySelector(`[name="${cssEscape(feedbackFor)}"]`)

      const input =
        (isVisible(inputByName) && inputByName) ||
        findInput(feedbackContainer || firstError.parentElement)

      const target = input || feedbackContainer || firstError

      target.scrollIntoView({
        behavior: "smooth",
        block: "center",
        inline: "nearest",
      })

      if (input?.focus) input.focus({ preventScroll: true })
    } else if (e.detail.on_clear) {
      // No visible errors — execute the encoded JS command (e.g. navigate)
      liveSocket.execJS(form, e.detail.on_clear)
    }
  })
})

window.addEventListener("enaia:dispatch-in-series", (e) => {
  const waitForElm = (selector) => {
    return new Promise((resolve) => {
      if (document.querySelector(selector)) {
        return resolve(document.querySelector(selector))
      }

      const observer = new MutationObserver((mutations) => {
        if (document.querySelector(selector)) {
          resolve(document.querySelector(selector))
          observer.disconnect()
        }
      })

      observer.observe(document.body, {
        childList: true,
        subtree: true,
      })
    })
  }
  e.detail.js_command.ops.forEach(async ([_opType, opOpts]) => {
    const target = await waitForElm(opOpts.to)
    const bubbles = opOpts.bubbles === undefined ? true : !!opOpts.bubbles
    const eventOpts = {
      bubbles: bubbles,
      cancelable: true,
      detail: opOpts.detail || {},
    }
    if (opOpts.event == "click") {
      target.dispatchEvent(new MouseEvent("click", eventOpts))
    } else {
      target.dispatchEvent(new CustomEvent(opOpts.event, eventOpts))
    }
  })
})

// connect if there are any LiveViews on the page
liveSocket.connect()
initializeMobileWebViewNavigation(liveSocket)

// Initialize Intercom if settings are available
initializeIntercom()

// expose liveSocket on window for web console debug logs and latency simulation:
// >> liveSocket.enableDebug()
// >> liveSocket.enableLatencySim(1000)  // enabled for duration of browser session
// >> liveSocket.disableLatencySim()
window.liveSocket = liveSocket
initOrphanedCidDetector()
initCidDebug()
window.Alpine = Alpine

Alpine.start()
logRocketInit()

async function logRocketInit() {
  const sessionData = document.body.dataset
  const logrocketEnabled = sessionData.logrocketEnabled
  const userId = sessionData.userId
  const userTraits = {
    name: sessionData.userName,
    email: sessionData.userEmail,
    internal: sessionData.userInternal == "true",
    screenSize: [screen.availWidth, screen.availHeight].join("x"),
    zoomLevel: zoomLevel(),
    company: sessionData.company,
    impersonatesUser: sessionData.userImpersonatesUser,
  }

  if (logrocketEnabled == "true") {
    const logrocketAppID = sessionData.logrocketAppId

    const logrocketProxy = "https://lr.enaia.co"
    window._lrAsyncScript = `${logrocketProxy}/cdn/logger.min.js`

    const module = await import("logrocket")
    let LogRocket = module.default
    LogRocket.init(logrocketAppID, {
      serverURL: `${logrocketProxy}/api/i`,
      release: sessionData.releaseVersion,
      ...searchPrivacy,
    })
    initializeFetchEmailDiagnostics(LogRocket, document, window)
    if (userId) {
      LogRocket.identify(userId, userTraits)
      LogRocket.getSessionURL((sessionURL) => {
        const bodyData = document.querySelector("body").dataset
        // Update Intercom with LogRocket session URL
        updateIntercom({
          custom_attributes: {
            logrocket_session_url: sessionURL,
            impersonates_user: bodyData.userImpersonatesUser,
          },
        })
      })
    }
  }
}

let copyTextTimeout
window.addEventListener("enaia:copy-text", (event) => {
  if ("clipboard" in navigator) {
    navigator.clipboard.writeText(event.target.dataset.toCopy)

    event.target.querySelector(".target--default-label").classList.add("hidden")

    event.target
      .querySelector(".target--copied-label")
      .classList.remove("hidden")

    clearTimeout(copyTextTimeout)
    copyTextTimeout = setTimeout(() => {
      event.target
        .querySelector(".target--default-label")
        .classList.remove("hidden")

      event.target
        .querySelector(".target--copied-label")
        .classList.add("hidden")
    }, 2000)
  } else {
    console.warn("Your browser doesn't support copying to clipboard :(")
  }
})

// A missing target degrades to a no-op instead of an uncaught TypeError, but it
// is never swallowed: every occurrence warns in ALL environments (ENA-9704).
// LogRocket captures console output, so an unexpected selector stays visible in
// production session replay — which is how these defects get found — rather than
// only on a developer machine. Warns should be zero in practice; any warn is a
// real defect (a stale or wrong selector) and should be treated as one.
window.addEventListener("phx:enaia:pushJS", (event) => {
  const encodedJS = JSON.stringify(event.detail.ops)
  const [eventType, eventDetails] = event.detail.ops[0]
  const el = document.querySelector(eventDetails.to)

  if (!el) {
    console.warn(
      "[enaia:pushJS] no element matches",
      eventDetails.to,
      event.detail.ops,
    )
    return
  }

  liveSocket.execJS(el, encodedJS, eventType)
})

window.addEventListener("phx:enaia:trigger-change", (event) => {
  const to = document.getElementById(event.detail.to)

  if (!to) {
    console.warn("[enaia:trigger-change] no element with id", event.detail.to)
    return
  }

  // "change" must be triggered on an input
  const inputOrForm =
    to.closest("input") || to.closest("form")?.querySelector("input")

  if (!inputOrForm) {
    console.warn("[enaia:trigger-change] no input found for", event.detail.to)
    return
  }

  inputOrForm.dispatchEvent(new Event("change", { bubbles: true }))
})

window.addEventListener(`phx:scrollDown`, (e) => {
  window.scrollTo({
    left: 0,
    top: document.body.scrollHeight,
    behavior: "smooth",
  })
})

window.addEventListener("scrollTo", (e) => {
  const el = document.querySelector(e.detail.selector)

  setTimeout(() => {
    el?.scrollIntoView({ block: "nearest", behavior: "smooth" })
  }, 150)
})

window.addEventListener("enaia:openIntercom", (event) => {
  // Intercom doesn't support prefilling messages directly,
  // but we can show the messenger with a custom message
  if (event.detail.subject || event.detail.text) {
    const message =
      `${event.detail.subject || ""}\n${event.detail.text || ""}`.trim()
    showNewMessage(message)
  } else {
    showIntercom()
  }
})

window.addEventListener("phx:enaia:openIntercom", (event) => {
  window.dispatchEvent(
    new CustomEvent("enaia:openIntercom", { detail: event.detail }),
  )
})

// LiveView compatibility - reinitialize Intercom on page transitions
// This handles transitions between authenticated and unauthenticated states
window.addEventListener("phx:page-loading-stop", () => {
  // Check if settings have changed (e.g., user logged in/out)
  if (window.intercomSettings) {
    initializeIntercom()
  }
})

import "./tab_change"
import "./autofocus"
import "./focus"
import "./submit_form"
import "./dropzone"

import OnlinePresence from "./online_presence.js"
// For testing purposes only:
window.testOnlinePresence = OnlinePresence

window.addEventListener("phx:copy-text", (event) => {
  let text = event.target.textContent.trim()

  navigator.clipboard?.writeText(text).then(() => {
    event.detail?.dispatcher?.classList.remove(event.detail?.class_to_remove)

    setTimeout(() => {
      event.detail?.dispatcher?.classList.add(event.detail?.class_to_remove)
    }, 3000)
  })
})

window.addEventListener("phx:copy-to-clipboard", (event) => {
  const text = event.detail?.text
  if (text) {
    navigator.clipboard?.writeText(text)
  }
})

window.addEventListener("phx:live_reload:attached", ({ detail: reloader }) => {
  // enable server log streaming to client
  // also needs the env var.
  // disable with reloader.disableServerLogs()
  reloader.enableServerLogs()
})

window.addEventListener("phx:image:reload", (event) => {
  setTimeout(() => {
    reloadImg(event.detail.url)
  }, 500)
})

window.addEventListener("turnstile:success", (e) => {
  const turnstiled = document.querySelectorAll(".turnstiled")
  turnstiled.forEach((el) => el.removeAttribute("disabled"))

  // Hide the loading text when Turnstile is ready
  const loadingTexts = document.querySelectorAll(".turnstile-loading-text")
  loadingTexts.forEach((el) => (el.style.display = "none"))
})

window.addEventListener("turnstile:expired", (e) => {
  const turnstiled = document.querySelectorAll(".turnstiled")
  turnstiled.forEach((el) => el.setAttribute("disabled", ""))

  // Show loading text again if Turnstile expires
  const loadingTexts = document.querySelectorAll(".turnstile-loading-text")
  loadingTexts.forEach((el) => (el.style.display = "block"))
})

window.addEventListener("phx:download_csv", (event) => {
  const { filename, data } = event.detail
  const blob = new Blob([data], { type: "text/csv" })
  const url = window.URL.createObjectURL(blob)
  const a = document.createElement("a")
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  window.URL.revokeObjectURL(url)
})

async function reloadImg(url) {
  res = await fetch(url, { cache: "reload", mode: "cors" })

  if (res.ok && res.redirected && res.type == "cors") {
    document.body.querySelectorAll(`img[src='${url}']`).forEach((img) => {
      time = Date.now()
      img.src = `${img.src}?t=${time}`

      if (img.srcset) {
        img.srcset = img.srcset.replaceAll(
          /(\/thumbnail\/\d+\/\d+\/\d+)/g,
          `$&?t=${time}`,
        )
      }
    })
  }
}
